import { convertLanguageToFormattedLanguage, Language } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useCallback, useRef } from 'react'
import { shouldFetchUnreleasedCopy } from '../translationDebug'

type FocalazyCacheData = {
  language: Language
  timestamp: string
  translations: Record<string, string>
}

const FOCALAZY_CACHE_KEY = 'translations-cache'
const FOCALAZY_API_BASE_URL = 'https://translations.faceup.com'
const FOCALAZY_PROJECT = 'monorepo'

const translationsSchema = yup.object().required()

const schema = yup.object({
  language: yup.string().oneOf(Object.values(Language)).required(),
  timestamp: yup.string().default(''),
  translations: translationsSchema,
})

const initialValue: FocalazyCacheData = {
  language: Language.En_Us,
  timestamp: '',
  translations: {},
}

const getCachedData = (
  language: Language,
  onError?: (error: unknown, context: { source: string; language: Language }) => void
): FocalazyCacheData => {
  try {
    const stored = localStorage.getItem(FOCALAZY_CACHE_KEY)
    if (!stored) {
      return initialValue
    }
    return schema.validateSync(JSON.parse(stored), { abortEarly: false })
  } catch (error) {
    onError?.(error, { source: 'focalazy-cache-read', language })
    return initialValue
  }
}

const setCachedData = (data: FocalazyCacheData): void => {
  try {
    schema.validateSync(data, { abortEarly: false })
    localStorage.setItem(FOCALAZY_CACHE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Failed to save Focalazy cache data:', error)
  }
}

type UseFetchMessagesOptions = {
  handleNewMessages: (messages: Record<string, string>, language: Language) => void
  onError?: (error: unknown, context: { source: string; language: Language }) => void
  onWarning?: (
    message: string,
    context: { source?: string; language: Language; extra?: Record<string, unknown> }
  ) => void
}

const isEmptyTranslations = (translations: Record<string, string>): boolean =>
  Object.keys(translations).length === 0

export const useFetchMessages = ({
  handleNewMessages,
  onError,
  onWarning,
}: UseFetchMessagesOptions) => {
  // Keep the callback props behind refs so `getMessages` / `forceRefetch`
  // can be `useCallback([])` — stable across renders. Callers that pass
  // inline `onError`/`onWarning` (a new function each render) would
  // otherwise force these returned functions to re-identify, which breaks
  // `useEffect`s that put them in their deps list and causes fetch loops.
  const handleNewMessagesRef = useRef(handleNewMessages)
  handleNewMessagesRef.current = handleNewMessages
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const onWarningRef = useRef(onWarning)
  onWarningRef.current = onWarning

  const setCacheData = useCallback((data: FocalazyCacheData) => {
    setCachedData(data)
  }, [])

  const fetchTranslations = useCallback(
    async (
      language: Language
    ): Promise<{ translations: Record<string, string>; timestamp: string }> => {
      try {
        const unreleased = shouldFetchUnreleasedCopy()
        const url = `${FOCALAZY_API_BASE_URL}/api/translations/${FOCALAZY_PROJECT}/${convertLanguageToFormattedLanguage(language)}${unreleased ? '?unreleased=true' : ''}`

        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch Focalazy translations: ${response.status}`)
        }

        const translations = translationsSchema.validateSync(await response.json())
        const timestamp = response.headers.get('X-Release-Timestamp') ?? ''

        return { translations, timestamp }
      } catch (error) {
        console.error('Failed to fetch Focalazy translations:', error)
        onErrorRef.current?.(error, { source: 'focalazy-fetch-translations', language })
        return { translations: {}, timestamp: '' }
      }
    },
    []
  )

  const getMessages = useCallback(
    async (language: Language): Promise<Record<string, string>> => {
      const currentCacheData = getCachedData(language, onErrorRef.current)
      const shouldUseCache =
        currentCacheData.timestamp !== '' && currentCacheData.language === language

      if (shouldUseCache) {
        const cachedTranslations = currentCacheData.translations
        const updateInBackground = async () => {
          try {
            const { translations: newTranslations, timestamp: apiTimestamp } =
              await fetchTranslations(language)

            if (apiTimestamp !== currentCacheData.timestamp && apiTimestamp !== '') {
              if (isEmptyTranslations(newTranslations)) {
                onWarningRef.current?.(
                  'Focalazy returned empty translations during background update',
                  { source: 'focalazy-empty-translations', language }
                )
                return
              }

              const newCacheData: FocalazyCacheData = {
                language,
                timestamp: apiTimestamp,
                translations: newTranslations,
              }
              setCacheData(newCacheData)
              handleNewMessagesRef.current(newTranslations, language)
            }
          } catch (error) {
            console.error('Failed to update Focalazy cache data:', error)
            onErrorRef.current?.(error, { source: 'focalazy-background-update', language })
          }
        }
        void updateInBackground()

        return cachedTranslations
      }

      const { translations, timestamp: apiTimestamp } = await fetchTranslations(language)

      if (isEmptyTranslations(translations)) {
        onWarningRef.current?.('Focalazy returned empty translations during initial fetch', {
          source: 'focalazy-empty-translations',
          language,
        })
        return translations
      }

      const newCacheData: FocalazyCacheData = {
        language,
        timestamp: apiTimestamp,
        translations,
      }

      setCacheData(newCacheData)

      return translations
    },
    [fetchTranslations, setCacheData]
  )

  // Force an API fetch bypassing the cache. Used by embedded-iframe edit
  // flows (focalazy) to pull newly-saved translations without a full reload.
  const forceRefetch = useCallback(
    async (language: Language): Promise<void> => {
      try {
        localStorage.removeItem(FOCALAZY_CACHE_KEY)
      } catch {
        // ignore
      }
      const { translations, timestamp } = await fetchTranslations(language)
      if (isEmptyTranslations(translations)) {
        onWarningRef.current?.('Focalazy returned empty translations during force refetch', {
          source: 'focalazy-empty-translations',
          language,
        })
        return
      }
      setCacheData({ language, timestamp, translations })
      handleNewMessagesRef.current(translations, language)
    },
    [fetchTranslations, setCacheData]
  )

  return { getMessages, forceRefetch }
}
